// Fechamento consolidado com filtros compartilháveis pela URL.
import { useEffect, useMemo, useState } from 'react';
import { Activity, CircleAlert, Clock3, RefreshCw, Scissors, Search, SlidersHorizontal } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { usePodeVerDetalhe } from '../ServiceLevelContext';
import { getIndustrias, getSignoff, SignoffResponse } from '../api';
import { getPrefs, getUser } from '../auth';
import SignoffTable from '../components/SignoffTable';
import KpiCard from '../components/KpiCard';
import JanelaRefSelect from '../components/JanelaRefSelect';
import MultiSelect from '../components/MultiSelect';
import Dica from '../components/Dica';
import { AJUDA } from '../utils/ajudas';
import { JanelaRef, Periodo, Recorte, SignoffLinha } from '../types';
import { fmtDataHoraFoto, fmtMoney, fmtPct, fmtPp } from '../utils/format';

const RECORTES: { id: Recorte; label: string }[] = [
  { id: 'SELLER', label: 'Sellers' },
  { id: 'INDUSTRIA', label: 'Indústrias' },
  { id: 'BU', label: 'BUs' },
];

type OrdKey = 'ns' | 'corte' | 'atraso' | 'nome';

const ORDENACOES: Record<OrdKey, { label: string; sort: (a: SignoffLinha, b: SignoffLinha) => number }> = {
  ns: { label: 'Pior NS primeiro', sort: (a, b) => (a.kpis.mtd.ns_qtd ?? 1) - (b.kpis.mtd.ns_qtd ?? 1) },
  corte: { label: 'Maior corte primeiro', sort: (a, b) => b.kpis.mtd.corte_valor - a.kpis.mtd.corte_valor },
  atraso: { label: 'Mais atrasos primeiro', sort: (a, b) => b.kpis.mtd.pedidos_em_atraso - a.kpis.mtd.pedidos_em_atraso },
  nome: { label: 'Nome (A–Z)', sort: (a, b) => a.label.localeCompare(b.label) },
};

export default function Dashboard() {
  const prefs = getPrefs();
  const user = getUser();
  const [params, setParams] = useSearchParams();
  const podeVerRecortes = usePodeVerDetalhe(2);
  const recorteParam = params.get('recorte');
  const recorte = RECORTES.some((r) => r.id === recorteParam)
    ? recorteParam as Recorte
    : user?.perfil === 'EXEC_INDUSTRIA' ? 'BU' : 'SELLER';
  const busca = podeVerRecortes ? params.get('q') || '' : '';
  const ordemParam = params.get('ord');
  const ordenar: OrdKey = ordemParam && Object.prototype.hasOwnProperty.call(ORDENACOES, ordemParam)
    ? ordemParam as OrdKey : 'ns';
  const industriasParam = podeVerRecortes ? params.get('industrias') || '' : '';
  const industriasSel = useMemo(
    () => Array.from(new Set(industriasParam.split(',').map(Number).filter((n) => Number.isInteger(n) && n > 0))),
    [industriasParam]
  );

  const setParam = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') next.delete(k); else next.set(k, v);
    }
    setParams(next, { replace: true });
  };

  const [ref, setRef] = useState<JanelaRef>(prefs.janela_ref || 'YTD');
  const [custom, setCustom] = useState<Periodo>({ de: prefs.custom_de, ate: prefs.custom_ate });
  const [industrias, setIndustrias] = useState<{ id: number; nome: string }[]>([]);
  const [data, setData] = useState<SignoffResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const [industriaError, setIndustriaError] = useState(false);
  const periodoInvalido = ref === 'CUSTOM' && (!custom.de || !custom.ate || custom.de > custom.ate);

  useEffect(() => {
    if (!podeVerRecortes) return;
    let ativo = true;
    setIndustriaError(false);
    getIndustrias().then((rows) => { if (ativo) setIndustrias(rows); })
      .catch(() => { if (ativo) setIndustriaError(true); });
    return () => { ativo = false; };
  }, [podeVerRecortes, retry]);

  useEffect(() => {
    let ativo = true;
    setData(null);
    setError(null);
    if (periodoInvalido) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getSignoff(recorte, ref, { de: custom.de, ate: custom.ate }, industriasSel)
      .then((response) => { if (ativo) setData(response); })
      .catch((e: unknown) => { if (ativo) setError(e instanceof Error ? e.message : 'Não foi possível carregar os indicadores.'); })
      .finally(() => { if (ativo) setLoading(false); });
    return () => { ativo = false; };
  }, [recorte, ref, custom.de, custom.ate, industriasSel, periodoInvalido, retry]);

  const linhas = useMemo(() => {
    if (!data) return [];
    const q = busca.trim().toLowerCase();
    return data.linhas
      .filter((l) => !q
        || l.label.toLowerCase().includes(q)
        || l.seller?.razao_social?.toLowerCase().includes(q)
        || l.seller?.cnpj?.includes(q))
      .sort(ORDENACOES[ordenar].sort);
  }, [data, busca, ordenar]);
  const chipsIndustrias = industrias.filter((i) => industriasSel.includes(i.id));
  const filtrosAtivos = Boolean(busca || industriasSel.length);
  const kpis = data?.total.kpis;

  return (
    <>
      <div className="page-header">
        <div>
          <span className="section-eyebrow">VISÃO GERAL</span>
          <h1 className="flex items-center gap-2">Nível de serviço <Dica titulo="Como ler o fechamento" texto={AJUDA.signoff} /></h1>
          <div className="sub">Acompanhe o atendimento, os prazos e os cortes da operação.</div>
        </div>
        <div className="filters dashboard-reference">
          <JanelaRefSelect value={ref} custom={custom} onChange={(r, c) => { setRef(r); if (c) setCustom(c); }} />
          {data && <span className="foto-badge">Atualizado em {fmtDataHoraFoto(data.ts_foto)}</span>}
        </div>
      </div>

      {podeVerRecortes && (
        <section className="card dashboard-filter-panel" aria-label="Filtros do fechamento">
          <div className="dashboard-section-header">
            <h2><SlidersHorizontal size={16} aria-hidden="true" /> Explore os resultados</h2>
            {filtrosAtivos && <button type="button" className="btn secondary" onClick={() => setParam({ q: null, industrias: null })}>Limpar filtros</button>}
          </div>
          <div className="recorte-tabs" aria-label="Agrupar resultados por">
            {RECORTES.map((r) => (
              <button type="button" key={r.id} aria-pressed={recorte === r.id} className={recorte === r.id ? 'on' : ''} onClick={() => setParam({ recorte: r.id })}>
                {r.label}
              </button>
            ))}
          </div>
          <div className="filters dashboard-filter-controls">
            <div className="filter-field filter-search">
              <label htmlFor="sl-dashboard-search">Buscar {RECORTES.find((r) => r.id === recorte)?.label.toLowerCase()}</label>
              <div className="search-field">
                <Search size={17} aria-hidden="true" />
                <input id="sl-dashboard-search" type="search" placeholder={recorte === 'SELLER' ? 'Nome, razão social ou CNPJ' : 'Buscar por nome'} value={busca} onChange={(e) => setParam({ q: e.target.value })} />
              </div>
            </div>
            <div className="filter-field">
              <label htmlFor="sl-dashboard-order">Ordenar resultados</label>
              <select id="sl-dashboard-order" value={ordenar} onChange={(e) => setParam({ ord: e.target.value })}>
                {Object.entries(ORDENACOES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div className="filter-field">
              <span className="filter-label">Filtrar por indústria</span>
              <MultiSelect rotulo="Indústrias" opcoes={industrias} selecionados={industriasSel} onChange={(ids) => setParam({ industrias: ids.join(',') })} />
            </div>
          </div>
          {industriaError && <div className="dashboard-feedback" role="status">As opções de indústria não puderam ser carregadas. <button type="button" className="kpi-click" onClick={() => setRetry((n) => n + 1)}>Tentar novamente</button></div>}
          {chipsIndustrias.length > 0 && (
            <div className="dashboard-filter-chips">
              {chipsIndustrias.map((i) => (
                <span key={i.id} className="seller-chip">{i.nome}<button type="button" onClick={() => setParam({ industrias: industriasSel.filter((s) => s !== i.id).join(',') })} aria-label={`Remover filtro ${i.nome}`}>×</button></span>
              ))}
              {recorte === 'SELLER' && <span className="muted text-xs">Indicadores calculados nos produtos das indústrias selecionadas.</span>}
            </div>
          )}
        </section>
      )}

      {periodoInvalido ? (
        <div className="card dashboard-state" role="status"><CircleAlert size={28} aria-hidden="true" /><h2>Defina o período de referência</h2><p>Selecione as datas inicial e final. A data final deve ser igual ou posterior à inicial.</p></div>
      ) : error ? (
        <div className="card dashboard-state" role="alert"><CircleAlert size={28} aria-hidden="true" /><h2>Não foi possível carregar o fechamento</h2><p>{error}</p><button type="button" className="btn" onClick={() => setRetry((n) => n + 1)}><RefreshCw size={16} aria-hidden="true" /> Tentar novamente</button></div>
      ) : loading || !data || !kpis ? (
        <div className="card dashboard-state" role="status" aria-live="polite"><RefreshCw size={28} aria-hidden="true" /><h2>Carregando indicadores</h2><p>Estamos preparando o fechamento da operação.</p></div>
      ) : (
        <>
          <div className="dashboard-section-header">
            <div><span className="section-eyebrow">FECHAMENTO DA OPERAÇÃO</span><h2>Indicadores consolidados</h2></div>
            <span className="muted text-xs">Mês corrente · comparação com o mês anterior</span>
          </div>
          <div className="kpi-row dashboard-summary">
            <KpiCard label="NS Quantidade" icon={<Activity size={19} />} valores={{ hoje: kpis.hoje.ns_qtd, mtd: kpis.mtd.ns_qtd, m1: kpis.m1.ns_qtd, ref: kpis.ref.ns_qtd }} refLabel={kpis.ref_label} m1Label={kpis.m1_label} formato={fmtPct} formatoDelta={fmtPp} extra={`${fmtPct(kpis.mtd.pct_apurado)} do volume faturado`} ajuda={AJUDA.ns} />
            <KpiCard label="Pedidos no prazo" icon={<Clock3 size={19} />} valores={{ hoje: kpis.hoje.pct_no_prazo, mtd: kpis.mtd.pct_no_prazo, m1: kpis.m1.pct_no_prazo, ref: kpis.ref.pct_no_prazo }} refLabel={kpis.ref_label} m1Label={kpis.m1_label} formato={fmtPct} formatoDelta={fmtPp} extra={`${kpis.mtd.pedidos_em_atraso.toLocaleString('pt-BR')} pedido(s) em atraso`} ajuda={AJUDA.prazo} />
            <KpiCard label="Corte" icon={<Scissors size={19} />} valores={{ hoje: kpis.hoje.pct_corte, mtd: kpis.mtd.pct_corte, m1: kpis.m1.pct_corte, ref: kpis.ref.pct_corte }} refLabel={kpis.ref_label} m1Label={kpis.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="down" extra={`${fmtMoney(kpis.mtd.corte_valor)} em cortes no mês`} ajuda={AJUDA.corte} />
          </div>
          <section className="card dashboard-signoff" aria-label="Fechamento nas quatro janelas">
            <div className="dashboard-section-header">
              <div><h2>{podeVerRecortes ? `Fechamento por ${RECORTES.find((r) => r.id === recorte)?.label.toLowerCase()}` : 'Fechamento consolidado'}</h2><p className="muted text-xs">Hoje, mês corrente, mês anterior e referência. Toque nos indicadores para ver o cálculo.</p></div>
              {podeVerRecortes && <span className="results-count">{linhas.length} {linhas.length === 1 ? 'resultado' : 'resultados'}{busca && ` de ${data.linhas.length}`}</span>}
            </div>
            <SignoffTable total={busca ? null : data.total} linhas={podeVerRecortes ? linhas : []} refLabel={kpis.ref_label} m1Label={kpis.m1_label} />
            {podeVerRecortes && linhas.length === 0 && <div className="dashboard-state"><Search size={28} aria-hidden="true" /><h2>Nenhum resultado encontrado</h2><p>{filtrosAtivos ? 'Ajuste a busca ou os filtros para encontrar outros resultados.' : 'Não há dados detalhados para este recorte.'}</p>{filtrosAtivos && <button type="button" className="btn secondary" onClick={() => setParam({ q: null, industrias: null })}>Limpar filtros</button>}</div>}
          </section>
          {!podeVerRecortes && <p className="chart-note">Seu acesso mostra os números consolidados. Para ver o detalhamento por seller, indústria ou pedido, fale com o administrador.</p>}
        </>
      )}
    </>
  );
}
